import type { FaqLocale } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos COMPONENTES de FAQ (ADR-0658/0720): mapeamento de erro, modal de criar/editar tópico
   e a tabela. O header/toolbar/toasts da PÁGINA ficam em `content/sections/faq.ts` (frente de
   routes/) — este arquivo é só o que `components/faq/` desenha por conta própria. */
export interface FaqAdminContent {
  readonly errors: {
    readonly networkFailure: string;
    readonly forbidden: string;
    readonly slugConflict: string;
    readonly genericFailure: string;
    readonly unexpected: string;
  };
  readonly localeLabels: Readonly<Record<FaqLocale, string>>;
  readonly formModal: {
    readonly editTitle: string;
    readonly createTitle: string;
    readonly missingFieldsError: string;
    readonly invalidFieldsError: (locale: string) => string;
    readonly updatedToast: string;
    readonly createdToast: string;
    readonly slugLabel: string;
    readonly categoryKeyLabel: string;
    /** Exemplos nos campos — o slug de exemplo é escrito no idioma do operador. */
    readonly slugPlaceholder: string;
    readonly categoryKeyPlaceholder: string;
    readonly orderLabel: string;
    readonly sharedInfo: string;
    readonly filledTitle: string;
    readonly categoryLabelLabel: string;
    readonly categoryLabelPlaceholderPt: string;
    readonly categoryLabelPlaceholderEn: string;
    readonly questionLabel: string;
    readonly questionPlaceholder: string;
    readonly answerLabel: string;
    readonly answerPlaceholder: string;
    readonly statusLabel: string;
    readonly statusDraft: string;
    readonly statusPublished: string;
    readonly cancel: string;
    readonly save: string;
    readonly create: string;
  };
  readonly table: {
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly clearFilters: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly createButton: string;
    readonly columnCategory: string;
    readonly columnQuestion: string;
    readonly columnLanguages: string;
    readonly columnOrder: string;
    readonly columnActions: string;
    readonly published: string;
    readonly draft: string;
    readonly edit: string;
    readonly remove: string;
    readonly selectPageAriaLabel: string;
    readonly selectRowAriaLabel: (question: string) => string;
  };
}

export const FAQ_ADMIN_CONTENT: Bilingual<FaqAdminContent> = {
  'pt-BR': {
    errors: {
      networkFailure: 'Falha de conexão. Tente novamente.',
      forbidden: 'Você não tem permissão para esta ação (ou sua sessão expirou).',
      slugConflict: 'Já existe uma entrada com esse slug neste idioma.',
      genericFailure: 'Não foi possível salvar a entrada. Tente novamente.',
      unexpected: 'Erro inesperado.',
    },
    localeLabels: { 'pt-BR': 'Português (BR)', 'en-US': 'English (US)' },
    formModal: {
      editTitle: 'Editar tópico',
      createTitle: 'Novo tópico',
      missingFieldsError: 'Preencha pelo menos um idioma (pergunta e resposta).',
      invalidFieldsError: (locale) =>
        `Verifique o slug e a chave de categoria (a-z, 0-9, - _) e o rótulo/campos do idioma ${locale}.`,
      updatedToast: 'Tópico atualizado.',
      createdToast: 'Tópico criado.',
      slugLabel: 'Slug (identidade)',
      categoryKeyLabel: 'Chave da categoria',
      slugPlaceholder: 'uso-da-ia',
      categoryKeyPlaceholder: 'capacidades',
      orderLabel: 'Ordem',
      sharedInfo:
        'Slug, chave da categoria e ordem são compartilhados pelo tópico. Rótulo da categoria, pergunta, resposta e status são por idioma — idiomas em branco são ignorados ao salvar.',
      filledTitle: 'Preenchido',
      categoryLabelLabel: 'Rótulo da categoria',
      categoryLabelPlaceholderPt: 'Capacidades',
      categoryLabelPlaceholderEn: 'Capabilities',
      questionLabel: 'Pergunta',
      questionPlaceholder: 'Como a IA é usada?',
      answerLabel: 'Resposta (markdown)',
      answerPlaceholder: 'Explique a capacidade em markdown…',
      statusLabel: 'Status',
      statusDraft: 'Rascunho',
      statusPublished: 'Publicado',
      cancel: 'Cancelar',
      save: 'Salvar',
      create: 'Criar',
    },
    table: {
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhum tópico corresponde aos filtros.',
      clearFilters: 'Limpar filtros',
      emptyTitle: 'Nenhum tópico ainda',
      emptyDescription: 'Crie a primeira pergunta ou explicação de capacidade.',
      createButton: 'Novo tópico',
      columnCategory: 'Categoria',
      columnQuestion: 'Pergunta',
      columnLanguages: 'Idiomas',
      columnOrder: 'Ordem',
      columnActions: 'Ações',
      published: 'Publicado',
      draft: 'Rascunho',
      edit: 'Editar',
      remove: 'Remover',
      selectPageAriaLabel: 'Selecionar tópicos visíveis',
      selectRowAriaLabel: (question) => `Selecionar tópico ${question}`,
    },
  },
  'en-US': {
    errors: {
      networkFailure: 'Connection failure. Try again.',
      forbidden: 'You do not have permission for this action (or your session expired).',
      slugConflict: 'An entry with that slug already exists in this language.',
      genericFailure: 'Could not save the entry. Try again.',
      unexpected: 'Unexpected error.',
    },
    localeLabels: { 'pt-BR': 'Português (BR)', 'en-US': 'English (US)' },
    formModal: {
      editTitle: 'Edit topic',
      createTitle: 'New topic',
      missingFieldsError: 'Fill in at least one language (question and answer).',
      invalidFieldsError: (locale) =>
        `Check the slug and category key (a-z, 0-9, - _) and the label/fields for ${locale}.`,
      updatedToast: 'Topic updated.',
      createdToast: 'Topic created.',
      slugLabel: 'Slug (identity)',
      categoryKeyLabel: 'Category key',
      slugPlaceholder: 'ai-usage',
      categoryKeyPlaceholder: 'capabilities',
      orderLabel: 'Order',
      sharedInfo:
        'Slug, category key, and order are shared by the topic. Category label, question, answer, and status are per language — blank languages are ignored on save.',
      filledTitle: 'Filled in',
      categoryLabelLabel: 'Category label',
      categoryLabelPlaceholderPt: 'Capacidades',
      categoryLabelPlaceholderEn: 'Capabilities',
      questionLabel: 'Question',
      questionPlaceholder: 'How is AI used?',
      answerLabel: 'Answer (markdown)',
      answerPlaceholder: 'Explain the capability in markdown…',
      statusLabel: 'Status',
      statusDraft: 'Draft',
      statusPublished: 'Published',
      cancel: 'Cancel',
      save: 'Save',
      create: 'Create',
    },
    table: {
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No topic matches the filters.',
      clearFilters: 'Clear filters',
      emptyTitle: 'No topics yet',
      emptyDescription: 'Create the first question or capability explanation.',
      createButton: 'New topic',
      columnCategory: 'Category',
      columnQuestion: 'Question',
      columnLanguages: 'Languages',
      columnOrder: 'Order',
      columnActions: 'Actions',
      published: 'Published',
      draft: 'Draft',
      edit: 'Edit',
      remove: 'Remove',
      selectPageAriaLabel: 'Select visible topics',
      selectRowAriaLabel: (question) => `Select topic ${question}`,
    },
  },
};

export const useFaqAdminContent = (): FaqAdminContent => useBilingual(FAQ_ADMIN_CONTENT);
